// Browser-side barge-in duck (design 4.3). Pure: fed one mic frame at a time,
// it says when to duck Otto and when to restore him. The player does the
// actual gain ramps (public/audio/otto-player-worklet.js).
//
//   Owner louder than the duck threshold for about 120 ms while Otto plays:
//     duck Otto to about 30 percent.
//   The server confirms with "interrupted": the player fades out over 30 ms
//     (the caller does that; this controller just resets).
//   No confirmation within 600 ms (a cough): restore the volume.
//
// The duck threshold is its own value, set ABOVE the mic gate, so echo residue
// on laptop speakers does not duck Otto's own voice. All values come from
// @otto/config DuckSettings (design starting points, ESTIMATE, not measured).
// The duck ships behind the DUCK_ENABLED switch, default OFF.
import { DuckSettings } from "@otto/config";

export type DuckAction = "duck" | "restore" | null;

export function defaultDuckSettings(): DuckSettings {
  return DuckSettings.parse({});
}

export function createDuckController(settings: DuckSettings) {
  let loudSince: number | null = null;
  let duckedAt: number | null = null;

  return {
    get ducked() {
      return duckedAt !== null;
    },
    /**
     * One mic frame. `now` in ms (frame time). `ottoPlaying` is the player's
     * own state. Returns the action to take this frame, if any.
     */
    frame(frameRms: number, ottoPlaying: boolean, now: number): DuckAction {
      if (duckedAt !== null) {
        if (!ottoPlaying) {
          // Otto stopped on his own (or the fade finished): nothing to restore.
          duckedAt = null;
          loudSince = null;
          return null;
        }
        if (now - duckedAt >= settings.confirm_timeout_ms) {
          duckedAt = null;
          loudSince = null;
          return "restore";
        }
        return null;
      }
      if (!ottoPlaying || frameRms < settings.threshold_rms) {
        loudSince = null;
        return null;
      }
      if (loudSince === null) loudSince = now;
      if (now - loudSince >= settings.hold_ms) {
        duckedAt = now;
        return "duck";
      }
      return null;
    },
    /** The server confirmed the interruption, or the turn ended: start fresh. */
    reset() {
      loudSince = null;
      duckedAt = null;
    },
  };
}

export type DuckController = ReturnType<typeof createDuckController>;
